import { useEffect, useRef, useState } from "react";
import { LOCALE_QUERY_PARAM } from "@/i18n/locales";

/**
 * Debug Terminal의 창 간 조율 (ADR 0036 결정 5)
 * --------------------------------------------------------------------------
 * 한 주체의 동시 세션은 1입니다(`MaxSessionsPerSubject`). 그래서 인라인 패널과 별도
 * Window는 자리를 **나눠 쓰는 것이 아니라 옮기는 것**입니다. 팝업이 자리를 잡으면
 * 인라인 패널은 세션을 놓고 "별도 Window 에서 연결되었습니다"만 말합니다.
 *
 * 조율은 `BroadcastChannel` 하나입니다. 서버에 묻지 않습니다 — 같은 브라우저의 두 창은
 * 서버가 구분할 이유가 없고, 물으면 화면 하나가 늘 때마다 폴링이 하나 늘어납니다.
 *
 * **채널은 신원을 증명하지 않습니다.** 같은 origin의 창이면 누구나 보낼 수 있으므로,
 * 이 신호는 화면 표시에만 씁니다. 세션이 실제로 열리는지는 서버가 정합니다 — 채널이
 * 조용해도 두 번째 연결은 서버의 동시성 거절 문구를 받습니다.
 */

const CHANNEL_NAME = "exec-session-window";

type ExecWindowMessage =
  /** 이 Pod의 세션 자리를 별도 Window가 잡았습니다. */
  | { type: "claimed"; podUid: string }
  /** 그 창이 세션을 놓았습니다(닫힘·종료). */
  | { type: "released"; podUid: string }
  /** 늦게 켜진 화면의 질의 — 잡고 있는 창이 있으면 `claimed`로 답합니다. */
  | { type: "query"; podUid: string };

/** 채널을 열지 못하는 환경(구형 브라우저·일부 테스트 런타임)에서는 조용히 없는 셈 칩니다. */
function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

function post(message: ExecWindowMessage): void {
  const ch = openChannel();
  if (!ch) return;
  try {
    ch.postMessage(message);
  } finally {
    ch.close();
  }
}

/**
 * 별도 Window 경로. 팝업과 인라인이 같은 문자열을 두 벌 갖지 않게 한 곳에 둡니다.
 *
 * **표시 언어를 함께 싣습니다**(ADR 0025). 새 창은 부모의 메모리를 물려받지 않고 언어를
 * 처음부터 정하는데, 그 규칙은 URL > 저장된 선택 > 브라우저입니다. `?lang=`으로 언어를
 * 강제한 화면은 그 선택을 저장하지 않으므로, 싣지 않으면 한국어로 보던 사용자의 팝업이
 * 영어로 뜹니다. 화면 간 이동에서 `lang`을 보존하는 것과 같은 이유입니다.
 */
export const debugTerminalWindowUrl = (params: {
  namespace: string;
  podName: string;
  podUid: string;
  locale?: string;
}): string => {
  /* namespace는 Pod의 신원이라 경로에 둡니다 — 상세 화면과 같은 규칙입니다. (#111) */
  const query = new URLSearchParams({ uid: params.podUid });
  if (params.locale) query.set(LOCALE_QUERY_PARAM, params.locale);
  return `/debug-terminal/${encodeURIComponent(params.namespace)}/${encodeURIComponent(params.podName)}?${query}`;
};

/**
 * 별도 Window를 엽니다. 차단되면 `null`입니다 — 부르는 쪽은 그 사실을 화면에 말하고
 * 인라인 패널을 그대로 둡니다. 팝업 차단은 오류가 아니라 사용자의 브라우저 설정입니다.
 */
export function openExecWindow(params: { namespace: string; podName: string; podUid: string; locale?: string }): Window | null {
  const features = "popup=yes,width=900,height=620,menubar=no,toolbar=no,location=no,status=no";
  /* 창 이름을 Pod UID로 고정합니다. 같은 Pod를 두 번 누르면 창이 하나 더 열리는 대신
     이미 열린 창이 앞으로 나옵니다 — 이름은 신원이 아니므로 UID를 씁니다. */
  const win = window.open(debugTerminalWindowUrl(params), `exec-${params.podUid}`, features);
  win?.focus();
  return win;
}

/**
 * 별도 Window 쪽에서 자리를 잡습니다. 창이 살아 있는 동안 `claimed`이고, 사라지면
 * `released`입니다. 늦게 켜진 인라인 화면의 `query`에도 답합니다 — 답하지 않으면
 * 그 화면은 자리가 비어 있다고 믿고 연결 버튼을 다시 보여 줍니다.
 */
export function useExecWindowClaim(podUid: string): void {
  useEffect(() => {
    if (!podUid) return;
    const ch = openChannel();
    post({ type: "claimed", podUid });
    const onMessage = (e: MessageEvent<ExecWindowMessage>) => {
      if (e.data?.type === "query" && e.data.podUid === podUid) post({ type: "claimed", podUid });
    };
    ch?.addEventListener("message", onMessage);
    /* 창이 사라질 때도 알립니다. 브라우저 X는 정리 훅을 부르지 않고 닫는 경로가 있어,
       unload에서 한 번 더 보냅니다 — 둘 다 놓쳐도 부모가 `closed` 폴링으로 회수합니다. */
    const onUnload = () => post({ type: "released", podUid });
    window.addEventListener("unload", onUnload);
    return () => {
      window.removeEventListener("unload", onUnload);
      ch?.removeEventListener("message", onMessage);
      ch?.close();
      post({ type: "released", podUid });
    };
  }, [podUid]);
}

/**
 * 인라인 패널 쪽에서 "별도 Window가 이 Pod의 자리를 잡았는가"를 봅니다.
 *
 * 채널 신호와 **자기가 연 창의 `closed`**를 함께 봅니다. 채널만 보면 강제 종료된 창의
 * `released`가 오지 않아 인라인 패널이 영영 양보한 채로 남고, `closed`만 보면 다른
 * 탭에서 연 창을 알아채지 못합니다.
 */
export function useExecWindowOwner(podUid: string): {
  claimed: boolean;
  /** 창을 엽니다. 팝업이 막히면 `false`이고, 그때 자리는 그대로 이 화면에 있습니다. */
  open: (params: { namespace: string; podName: string; locale?: string }) => boolean;
} {
  const [claimed, setClaimed] = useState(false);
  const child = useRef<Window | null>(null);

  useEffect(() => {
    if (!podUid) return;
    const ch = openChannel();
    const onMessage = (e: MessageEvent<ExecWindowMessage>) => {
      const msg = e.data;
      if (!msg || msg.podUid !== podUid) return;
      if (msg.type === "claimed") setClaimed(true);
      else if (msg.type === "released") setClaimed(false);
    };
    ch?.addEventListener("message", onMessage);
    /* 이 화면이 늦게 켜졌을 수 있습니다 — 이미 열린 창이 있으면 답이 옵니다. */
    post({ type: "query", podUid });

    /* 자기가 연 창이 사라지면 자리를 회수합니다. 1초는 사람이 "닫았는데 화면이
       그대로"라고 느끼기 전이고, 창 하나를 보는 비용으로 충분히 쌉니다. */
    const timer = window.setInterval(() => {
      if (child.current?.closed) {
        child.current = null;
        setClaimed(false);
      }
    }, 1_000);

    return () => {
      window.clearInterval(timer);
      ch?.removeEventListener("message", onMessage);
      ch?.close();
    };
  }, [podUid]);

  return {
    claimed,
    open: ({ namespace, podName, locale }) => {
      const win = openExecWindow({ namespace, podName, podUid, locale });
      if (!win) return false;
      child.current = win;
      /* 창이 `claimed`를 보낼 때까지 기다리지 않습니다 — 기다리면 그 사이에 인라인
         패널이 연결 버튼을 계속 보여 주고, 사용자는 자리를 두 번 잡으려 합니다. */
      setClaimed(true);
      return true;
    },
  };
}
